import styled from "styled-components";
import EmailEditor from "react-email-editor";
import { useMediaQuery } from "react-responsive";
import { Link } from "react-router-dom";

import * as css from "styles/CssVariables.js";
import globalConstants from "constants/globalConstants";
import ButtonPrimary from "components/buttons/ButtonPrimary";
import InputWithLabel from "components/forms/InputWithLabel";
import InputText from "components/forms/InputText";

import bulbIcon from "assets/img/icons/tip-bulb.svg";
import newTabIcon from "assets/img/icons/Expand-Window.svg";
import ButtonPrimarySmall from "components/buttons/ButtonPrimarySmall";

const EditorWrap = styled.div`
  width: 100%;
  padding: 0 25px;
  box-sizing: border-box;

  display: flex;
  flex-direction: row;
  gap: ${css.VERTICAL_BOX_MARGIN_LARGE};

  flex: 1;

  @media (max-width: ${globalConstants.switchTablet}) {
    padding: 0 8px;
    flex-direction: column;
    gap: ${css.VERTICAL_BOX_MARGIN_XLARGE};
  }
`;

const EditorBorderWrap = styled.div`
  position: relative;

  border-radius: ${css.BOX_RADIUS_SECONDARY};
  overflow: hidden;

  display: flex;

  flex: 1;
`;

const EditorInputsColumn = styled.div`
  width: 100%;
  max-width: 350px;
  box-sizing: border-box;

  display: flex;
  flex-direction: column;
  gap: ${css.VERTICAL_BOX_MARGIN_LARGE};
  justify-content: space-between;

  @media (max-width: ${globalConstants.switchTablet}) {
    padding: 0 12px;
  }
`;

const EditorInputs = styled.div`
  width: 100%;

  display: flex;
  flex-direction: column;

  gap: ${css.VERTICAL_BOX_MARGIN_MEDIUM};
`;

const EditorControls = styled.div`
  padding: 0;

  display: flex;
  flex-direction: column;
  gap: ${css.VERTICAL_BOX_MARGIN};

  @media (max-width: ${globalConstants.switchTablet}) {
    padding: 0 12px;
  }
`;

const ButtonWrap = styled.div`
  display: flex;
`;

const Warning = styled.div`
  margin-top: 7px;

  font-size: 13px;
  font-weight: 500;
  color: ${css.ERROR_RED};
`;

const MergeTagsTipBox = styled.div`
  width: 100%;
  padding: ${css.BOX_PADDING_SECONDARY};
  margin-top: 8px;
  box-sizing: border-box;

  background: ${css.FLAT_BOX_BACKGROUND};
  border-radius: ${css.BOX_RADIUS_SECONDARY};

  display: flex;
  gap: 8px;
`;

const TipBoxContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${css.VERTICAL_BOX_MARGIN};
`;

const TipIcon = styled.div`
  width: 22px;
  min-width: 22px;
  height: 22px;
`;

const TipText = styled.div``;

const ExternalLink = styled(Link)`
  display: flex;
  align-items: center;
  gap: 6px;

  text-decoration: none;
`;

const ExternalLinkText = styled.div`
  color: ${css.LIGHT_BLUE};
`;

const ExternalLinkIcon = styled.div`
  padding-top: 2px;

  width: 11px;
  min-width: 11px;
`;

const Templates = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const TemplatesTitle = styled.div`
  font-weight: 600;
`;

const TemplatesText = styled.div``;

const TemplatesButtonWrap = styled.div`
  padding-top: 4px;

  display: flex;
`;

const MobileOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 10000;

  background: rgba(0, 0, 0, 0.8);

  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: ${css.VERTICAL_BOX_MARGIN};
`;

const MobileOverlayText = styled.div`
  max-width: 400px;
  padding: 0 20px;

  font-weight: 500;
  text-align: center;
  line-height: 130%;
`;

const MobileOverlayButtonWrap = styled.div`
  display: flex;
`;

export default ({
  emailEditorRef,
  saveMessage,
  onEditorReady,
  loadingSaveMessage,
  subject,
  designLoaded,
  onSubjectInputChange,
  warnings,
  openTemplatesModal,
  mobileOverlay,
  closeMobileOverlay,
}) => {
  const tabletScreen = useMediaQuery({
    query: `(max-width: ${globalConstants.switchTablet})`,
  });

  return (
    <EditorWrap>
      <EditorInputsColumn>
        <EditorInputs>
          <InputWithLabel label="Subject">
            <InputText
              type="text"
              value={subject}
              placeholder={designLoaded ? "Enter email subject" : "Loading..."}
              readOnly={!designLoaded}
              onChange={onSubjectInputChange}
              warning={warnings.includes("subject")}
              maxLength="120"
            />
          </InputWithLabel>
          <MergeTagsTipBox>
            <TipIcon>
              <img src={bulbIcon} style={{ width: "100%" }} />
            </TipIcon>
            <TipBoxContent>
              <TipText>
                Use merge tags to dynamically insert the recipient's name into
                the email.
              </TipText>
              <TipText>
                <ExternalLink
                  to="https://help.scalelup.com/en/articles/10542199-what-is-the-minimum-amount-for-withdrawal"
                  target="_blank"
                >
                  <ExternalLinkText>Learn about merge tags</ExternalLinkText>
                  <ExternalLinkIcon>
                    <img src={newTabIcon} style={{ width: "100%" }} />
                  </ExternalLinkIcon>
                </ExternalLink>
              </TipText>
            </TipBoxContent>
          </MergeTagsTipBox>
          <Templates>
            <TemplatesTitle>Email Templates</TemplatesTitle>
            <TemplatesText>Start with a pre-made design.</TemplatesText>
            <TemplatesButtonWrap>
              <ButtonPrimarySmall onClick={openTemplatesModal}>
                Load Template
              </ButtonPrimarySmall>
            </TemplatesButtonWrap>
          </Templates>
        </EditorInputs>
        {!tabletScreen && (
          <EditorControls>
            {warnings.length !== 0 && (
              <Warning>
                There are errors in the form. Please check the entered
                information.
              </Warning>
            )}
            <ButtonWrap>
              <ButtonPrimary
                onClick={saveMessage}
                disabled={loadingSaveMessage || !designLoaded}
              >
                Save
              </ButtonPrimary>
            </ButtonWrap>
          </EditorControls>
        )}
      </EditorInputsColumn>
      <EditorBorderWrap>
        <EmailEditor
          ref={emailEditorRef}
          onReady={onEditorReady}
          options={{
            mergeTags: {
              first_name: {
                name: "Name",
                value: "{{name}}",
                sample: "John",
              },
            },
            // defaultDevice: "mobile",
          }}
          projectId={277672}
        />
        {mobileOverlay && (
          <MobileOverlay>
            <MobileOverlayText>
              The editor is not optimized for use on mobile devices with a
              narrow screen. Consider using it on a larger screen, such as a
              laptop or desktop computer.
            </MobileOverlayText>
            <MobileOverlayButtonWrap>
              <ButtonPrimary onClick={closeMobileOverlay}>OK</ButtonPrimary>
            </MobileOverlayButtonWrap>
          </MobileOverlay>
        )}
      </EditorBorderWrap>
      {tabletScreen && (
        <EditorControls>
          <ButtonWrap>
            <ButtonPrimary
              onClick={saveMessage}
              disabled={loadingSaveMessage || !designLoaded}
            >
              Save
            </ButtonPrimary>
          </ButtonWrap>
          {warnings.length !== 0 && (
            <Warning>
              There are errors in the form. Please check the entered
              information.
            </Warning>
          )}
        </EditorControls>
      )}
    </EditorWrap>
  );
};
